import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  AccountBalanceWallet as WalletIcon,
  Payments as PaymentsIcon,
  AccountBalanceWalletOutlined as SafeHavenIcon,
  Payment as PayscribeIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

function SystemBalance() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState(null);
  const [openFundDialog, setOpenFundDialog] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [fundLoading, setFundLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch SafeHaven, Payscribe, and System Wallet balances
      const [safeHavenRes, payscribeRes, systemWalletRes] = await Promise.all([
        api.dashboard.getSafeHavenBalance(),
        api.dashboard.getPayscribeBalance(),
        api.dashboard.getSystemWalletBalance()
      ]);

      console.log('SafeHaven Response:', safeHavenRes);
      console.log('Payscribe Response:', payscribeRes);
      console.log('System Wallet Response:', systemWalletRes);

      // Extract balances from the correct response structure
      const safeHavenBalance = safeHavenRes?.balance || 0;
      const payscribeBalance = payscribeRes.balance || 0;
      const systemBalance = systemWalletRes.balance || 0;
      
      console.log('SafeHaven Balance:', safeHavenBalance);
      console.log('Payscribe Balance:', payscribeBalance);
      console.log('System Balance:', systemBalance);
      
      const newBalance = {
        safeHaven: safeHavenBalance,
        payscribe: payscribeBalance,
        system: systemBalance,
        total: safeHavenBalance + payscribeBalance + systemBalance
      };

      console.log('Final Balance State:', newBalance);
      setBalance(newBalance);

      // Fetch recent system wallet funding transactions
      const transactionsRes = await api.dashboard.getAllTransactions();
      const transactionsData = transactionsRes?.data || transactionsRes || [];
      const systemWalletTransactions = Array.isArray(transactionsData) 
        ? transactionsData
            .filter(tx => tx.purpose === 'System Wallet Funding' || tx.type === 'System Wallet Funding')
            .slice(0, 10)
        : [];
      setTransactions(systemWalletTransactions);
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch data';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      showSnackbar('Data refreshed successfully', 'success');
    } catch (error) {
      // Error is already handled in fetchData
    } finally {
      setRefreshing(false);
    }
  };

  const handleOpenFundDialog = () => {
    setOpenFundDialog(true);
  };

  const handleCloseFundDialog = () => {
    setOpenFundDialog(false);
    setFundAmount('');
  };

  const handleCloseFundDialogAfterSuccess = () => {
    setOpenFundDialog(false);
    setFundAmount('');
    showSnackbar('System wallet funded successfully!', 'success');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleFundWallet = async () => {
    try {
      if (!fundAmount || Number(fundAmount) <= 0) {
        showSnackbar('Please enter a valid amount', 'error');
        return;
      }

      setFundLoading(true);

      // Call the backend API to fund the system wallet
      const response = await api.dashboard.fundSystemWallet({ amount: Number(fundAmount) });
      
      if (response.success) {
        // Show success message
        showSnackbar(`Successfully funded system wallet with ₦${Number(fundAmount).toLocaleString()}`, 'success');
        handleCloseFundDialog();
        
        // Refresh balances after funding
        await fetchData();
      } else {
        showSnackbar(response.message || 'Failed to fund system wallet', 'error');
      }
    } catch (error) {
      console.error('Error funding wallet:', error);
      showSnackbar(error.message || 'Error funding system wallet', 'error');
    } finally {
      setFundLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return <LoadingSpinner size="medium" />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Balance Cards */}
      <Box sx={{ width: '100%', mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                background: theme => `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                boxShadow: theme => `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SafeHavenIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                    SafeHaven Balance
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
                  ₦{balance?.safeHaven?.toLocaleString() || '0'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                background: theme => `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                boxShadow: theme => `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PayscribeIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                    Payscribe Balance
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
                  ₦{balance?.payscribe?.toLocaleString() || '0'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                background: theme => `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                boxShadow: theme => `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WalletIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                    System Wallet
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
                  ₦{balance?.system?.toLocaleString() || '0'}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOpenFundDialog}
                  startIcon={<PaymentsIcon />}
                  sx={{
                    mt: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    },
                  }}
                >
                  Fund Wallet
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                background: theme => `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                boxShadow: theme => `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountBalanceIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                    Total Balance
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
                  ₦{balance?.total?.toLocaleString() || '0'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Transactions List */}
      <Paper 
        sx={{ 
          borderRadius: 3, 
          overflow: 'hidden',
          boxShadow: theme => `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: theme => `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Recent System Wallet Funding Transactions
            </Typography>
            <Tooltip title="Refresh">
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Purpose</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow 
                  key={transaction._id}
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {transaction.type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      ₦{transaction.amount?.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {transaction.purpose}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.status}
                      size="small"
                      color={
                        transaction.status === 'success' ? 'success' :
                        transaction.status === 'pending' ? 'warning' :
                        'error'
                      }
                      sx={{
                        borderRadius: 1,
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDistanceToNow(new Date(transaction.createdAt), { addSuffix: true })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small"
                        sx={{
                          color: 'primary.main',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Fund Wallet Dialog */}
      <Dialog 
        open={openFundDialog} 
        onClose={handleCloseFundDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme => `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: theme => `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        }}>
          Fund System Wallet
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={fundAmount}
            onChange={(e) => setFundAmount(e.target.value)}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>₦</Typography>,
            }}
            sx={{ 
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseFundDialog}
            disabled={fundLoading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleFundWallet}
            variant="contained"
            disabled={!fundAmount || Number(fundAmount) <= 0 || fundLoading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 100,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              },
            }}
          >
            {fundLoading ? 'Funding...' : 'Fund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ 
          zIndex: 9999,
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SystemBalance; 